import AdmissionItem from '@/components/booking-summary/AdmissionItem';
import { TransText } from '@/i18n/trans/text';
import type { AdmissionDTO, BookingDTO } from '@/types/dto/agent';
import type { CheckoutOrBookingPassengerMap } from '@/types/passenger';
import type { TravelAccountsMap } from '@/types/travelpass';
import { cn } from '@/utils/cn';
import { format } from '@/utils/dateTime';
import { getPassengerName } from '@/utils/passenger';
import { Icons, Typography } from '@packages/webshop-design-system';
import type { FC } from 'react';

interface NonTrips {
  nonTripsOffers: NonNullable<BookingDTO['nonTripOffers']>;
  passengersMap: CheckoutOrBookingPassengerMap;
  travelAccountsMap: TravelAccountsMap | undefined;
}

const NonTrips: FC<NonTrips> = ({
  nonTripsOffers,
  travelAccountsMap,
  passengersMap,
}) => {
  const grayedOutStatuses: Array<AdmissionDTO['status']> = [
    'REFUNDED',
    'RELEASED',
    'EXCHANGED',
  ];
  let passengerIndex = -1;

  return (
    <div className="flex flex-col">
      <div className="flex gap-2 rounded-t-md bg-primary-pale px-4 py-3">
        <Icons.transportFrom className="h-5 w-5 text-primary" />
        <Typography variant="body1-bold">
          <TransText i18nKey="travelPassDetails" />
        </Typography>
      </div>
      <div className="flex flex-col gap-6 p-4">
        {nonTripsOffers.map((offer, key) => {
          const firstAdmission = offer.admissions?.[0];
          const firstFullfilment = firstAdmission?.fulfillments?.[0];
          const firstTravelAccount =
            travelAccountsMap?.[
              `${firstFullfilment?.issuer}-${firstFullfilment?.controlNumber}`
            ];

          if (
            !firstTravelAccount ||
            !('regionalConstraint' in firstTravelAccount)
          ) {
            return null;
          }

          const zone =
            firstTravelAccount.regionalConstraint?.regionalValidities[0].zone;
          const isTripGrayedOut = offer.admissions?.every((admission) =>
            grayedOutStatuses.includes(admission.status)
          );

          return (
            <div className="flex flex-col gap-4" key={key}>
              <div className="flex flex-col gap-3">
                <div className="flex gap-1">
                  <Typography className="text-neutral" variant="body2">
                    <TransText i18nKey="activation" />
                    &#58;
                  </Typography>
                  {firstAdmission?.validFrom && (
                    <Typography variant="body2-bold">
                      {format(firstAdmission?.validFrom, 'dd MMM, yyyy')}
                    </Typography>
                  )}
                </div>
                <div className="flex flex-row gap-2">
                  <div className="flex w-fit flex-col">
                    <div className="h-4 w-4">
                      <Icons.destination
                        height={12}
                        width={12}
                        className={cn('mx-auto text-primary', {
                          'text-neutral': isTripGrayedOut,
                        })}
                      />
                    </div>
                    <div className="mx-auto h-full w-0.5 bg-neutral-light" />
                    <div className="h-4 w-4">
                      <Icons.destination
                        height={12}
                        width={12}
                        className={cn('mx-auto text-primary', {
                          'text-neutral': isTripGrayedOut,
                        })}
                      />
                    </div>
                  </div>
                  <div className="flex flex-col gap-2">
                    <Typography
                      variant="body2-bold"
                      className={cn({
                        'text-neutral line-through decoration-error-border':
                          isTripGrayedOut,
                      })}
                    >
                      {zone?.entryStation?.name}
                    </Typography>
                    <Typography
                      variant="body2-bold"
                      className={cn({
                        'text-neutral line-through decoration-error-border':
                          isTripGrayedOut,
                      })}
                    >
                      {zone?.terminalStation?.name}
                    </Typography>
                  </div>
                </div>
              </div>
              {(offer.admissions ?? []).map((admission, key) => {
                const isGrayedOut = grayedOutStatuses.includes(
                  admission.status
                );

                passengerIndex++;

                return (
                  <div className="flex flex-col gap-3" key={key}>
                    {(admission.passengerIds ?? []).map((passengerId, key) => {
                      const passenger = passengersMap[passengerId];

                      if (!passenger) {
                        return null;
                      }

                      return (
                        <div className="flex flex-col" key={key}>
                          <Typography
                            variant="body2-bold"
                            className={cn({
                              'text-neutral': isGrayedOut,
                            })}
                          >
                            {getPassengerName(
                              {
                                firstName: passenger.firstName,
                                lastName: passenger.lastName,
                              },
                              passengerIndex
                            )}
                          </Typography>
                          <div className="ml-6 mt-2 flex flex-col gap-1">
                            <AdmissionItem
                              icon="ticket"
                              description={admission.productSummary}
                              descriptiveTexts={
                                admission.productDescriptiveTexts
                              }
                              amount={admission.price?.amount || 0}
                              status={admission.status}
                              promoCode={admission.appliedReductions?.[0]}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default NonTrips;
